/**
 * Framework mappings for security checks
 * Maps check titles to relevant framework controls
 * Updated: May 2026 — added PCI DSS v4.0, DORA, NIS2 mappings
 */

export const frameworkMappings = {
  // Identity & Access Management
  'MFA enabled for root account': {
    'CSA CCM v4': 'IAM-02, IAM-10',
    'ISO 27001:2022': 'A.9.4.3',
    'NIST CSF 2.0': 'PR.AA-01, PR.AA-03',
    'CIS Controls v8': '4.5, 5.3',
    'SOC 2 Type II': 'CC6.1, CC6.3',
    'PCI DSS v4.0': 'Req. 8.4.2',
    'DORA': 'Art. 9(4)(c)',
    'NIS2': 'Art. 21(2)(j)'
  },
  'IAM password policy configured': {
    'CSA CCM v4': 'IAM-09',
    'ISO 27001:2022': 'A.9.4.3',
    'NIST CSF 2.0': 'PR.AA-01',
    'CIS Controls v8': '4.1, 4.2',
    'SOC 2 Type II': 'CC6.1',
    'PCI DSS v4.0': 'Req. 8.3.6',
    'DORA': 'Art. 9(4)(c)',
    'NIS2': 'Art. 21(2)(i)'
  },
  'No hardcoded access keys': {
    'CSA CCM v4': 'IAM-03, CEK-03',
    'ISO 27001:2022': 'A.9.2.3',
    'NIST CSF 2.0': 'PR.AA-01, PR.AA-05',
    'CIS Controls v8': '4.6',
    'SOC 2 Type II': 'CC6.1',
    'PCI DSS v4.0': 'Req. 6.2.4, 8.6.1',
    'DORA': 'Art. 9(4)(c)',
    'NIS2': 'Art. 21(2)(i)'
  },
  'Least privilege principle applied': {
    'CSA CCM v4': 'IAM-05',
    'ISO 27001:2022': 'A.9.2.3',
    'NIST CSF 2.0': 'PR.AA-05',
    'CIS Controls v8': '4.7',
    'SOC 2 Type II': 'CC6.1, CC6.3',
    'PCI DSS v4.0': 'Req. 7.2.1',
    'DORA': 'Art. 9(4)(a)',
    'NIS2': 'Art. 21(2)(i)'
  },
  'Azure AD Conditional Access policies': {
    'CSA CCM v4': 'IAM-10, IAM-11',
    'ISO 27001:2022': 'A.9.4.1, A.9.4.2',
    'NIST CSF 2.0': 'PR.AA-02, PR.AA-05',
    'CIS Controls v8': '4.3',
    'SOC 2 Type II': 'CC6.1',
    'PCI DSS v4.0': 'Req. 8.4',
    'DORA': 'Art. 9(4)(c)',
    'NIS2': 'Art. 21(2)(j)'
  },

  // Network Security
  'Security groups restrict inbound traffic': {
    'CSA CCM v4': 'IVS-06',
    'ISO 27001:2022': 'A.13.1.1',
    'NIST CSF 2.0': 'PR.AA-05, PR.IR-01',
    'CIS Controls v8': '9.2, 9.4',
    'SOC 2 Type II': 'CC6.6',
    'PCI DSS v4.0': 'Req. 1.3.1',
    'DORA': 'Art. 9(4)(b)',
    'NIS2': 'Art. 21(2)(a)'
  },
  'VPC flow logs enabled': {
    'CSA CCM v4': 'LOG-01, LOG-02',
    'ISO 27001:2022': 'A.12.4.1',
    'NIST CSF 2.0': 'DE.CM-01',
    'CIS Controls v8': '8.2, 8.5',
    'SOC 2 Type II': 'CC7.2',
    'PCI DSS v4.0': 'Req. 10.3.2',
    'DORA': 'Art. 10',
    'NIS2': 'Art. 21(2)(a)'
  },
  'Private subnets for databases': {
    'CSA CCM v4': 'IVS-06, IVS-09',
    'ISO 27001:2022': 'A.13.1.3',
    'NIST CSF 2.0': 'PR.AA-05',
    'CIS Controls v8': '9.2',
    'SOC 2 Type II': 'CC6.6',
    'PCI DSS v4.0': 'Req. 1.3.2',
    'DORA': 'Art. 9(4)(b)',
    'NIS2': 'Art. 21(2)(a)'
  },
  'WAF enabled for web applications': {
    'CSA CCM v4': 'AIS-04',
    'ISO 27001:2022': 'A.14.2.5',
    'NIST CSF 2.0': 'PR.PS-04',
    'CIS Controls v8': '9.6, 16.13',
    'SOC 2 Type II': 'CC6.6, CC6.8',
    'PCI DSS v4.0': 'Req. 6.4.1',
    'DORA': 'Art. 9(4)(b)',
    'NIS2': 'Art. 21(2)(e)'
  },

  // Data Protection
  'S3 buckets encrypted at rest': {
    'CSA CCM v4': 'CEK-03, DSP-03',
    'ISO 27001:2022': 'A.8.2.3, A.10.1.1',
    'NIST CSF 2.0': 'PR.DS-01',
    'CIS Controls v8': '3.11',
    'SOC 2 Type II': 'CC6.1, CC6.7',
    'PCI DSS v4.0': 'Req. 3.5.1',
    'DORA': 'Art. 9(4)(e)',
    'NIS2': 'Art. 21(2)(h)'
  },
  'S3 bucket policies restrict public access': {
    'CSA CCM v4': 'DSP-16',
    'ISO 27001:2022': 'A.8.2.3',
    'NIST CSF 2.0': 'PR.AA-05, PR.DS-01',
    'CIS Controls v8': '3.3',
    'SOC 2 Type II': 'CC6.6',
    'PCI DSS v4.0': 'Req. 7.2.2',
    'DORA': 'Art. 9(4)(e)',
    'NIS2': 'Art. 21(2)(i)'
  },
  'RDS encryption enabled': {
    'CSA CCM v4': 'CEK-03, DSP-03',
    'ISO 27001:2022': 'A.10.1.1',
    'NIST CSF 2.0': 'PR.DS-01',
    'CIS Controls v8': '3.11',
    'SOC 2 Type II': 'CC6.7',
    'PCI DSS v4.0': 'Req. 3.5.1',
    'DORA': 'Art. 9(4)(e)',
    'NIS2': 'Art. 21(2)(h)'
  },

  // Monitoring & Compliance
  'CloudTrail enabled in all regions': {
    'CSA CCM v4': 'LOG-01, LOG-02, LOG-03',
    'ISO 27001:2022': 'A.12.4.1, A.12.4.3',
    'NIST CSF 2.0': 'DE.CM-01, DE.CM-03',
    'CIS Controls v8': '8.5, 8.6',
    'SOC 2 Type II': 'CC7.2',
    'PCI DSS v4.0': 'Req. 10.2.1',
    'DORA': 'Art. 10, Art. 11',
    'NIS2': 'Art. 21(2)(a)'
  },
  'CloudWatch alarms configured': {
    'CSA CCM v4': 'SEF-03, SEF-04',
    'ISO 27001:2022': 'A.16.1.2, A.16.1.4',
    'NIST CSF 2.0': 'DE.AE-06, RS.CO-02',
    'CIS Controls v8': '6.7, 8.6',
    'SOC 2 Type II': 'CC7.2, CC7.3',
    'PCI DSS v4.0': 'Req. 10.7.1',
    'DORA': 'Art. 11',
    'NIS2': 'Art. 21(2)(b)'
  },

  // Container Security
  'Container image scanning': {
    'CSA CCM v4': 'AIS-04, CCC-02',
    'ISO 27001:2022': 'A.12.6.1, A.14.2.8',
    'NIST CSF 2.0': 'ID.RA-01, PR.DS-06',
    'CIS Controls v8': '3.6, 16.13',
    'SOC 2 Type II': 'CC8.1',
    'PCI DSS v4.0': 'Req. 6.3.3',
    'DORA': 'Art. 9(4)(d)',
    'NIS2': 'Art. 21(2)(e)'
  },
  'Container runtime security': {
    'CSA CCM v4': 'UEM-04, IVS-06',
    'ISO 27001:2022': 'A.12.2.1, A.12.5.1',
    'NIST CSF 2.0': 'DE.CM-04',
    'CIS Controls v8': '10.5, 13.7',
    'SOC 2 Type II': 'CC6.8',
    'PCI DSS v4.0': 'Req. 5.3.3',
    'DORA': 'Art. 9(4)(d)',
    'NIS2': 'Art. 21(2)(e)'
  },

  // API Security
  'API Gateway authentication': {
    'CSA CCM v4': 'IAM-02, IAM-08',
    'ISO 27001:2022': 'A.9.4.2, A.14.1.3',
    'NIST CSF 2.0': 'PR.AA-01, PR.AA-05',
    'CIS Controls v8': '4.7, 16.11',
    'SOC 2 Type II': 'CC6.1',
    'PCI DSS v4.0': 'Req. 8.2.1',
    'DORA': 'Art. 9(4)(c)',
    'NIS2': 'Art. 21(2)(i)'
  },

  // Advanced Security Features
  'AWS Shield Advanced enabled': {
    'CSA CCM v4': 'IVS-04, SEF-03',
    'ISO 27001:2022': 'A.13.1.1, A.17.1.2',
    'NIST CSF 2.0': 'PR.IR-01, DE.CM-01',
    'CIS Controls v8': '9.4, 13.9',
    'SOC 2 Type II': 'CC6.8, A1.2',
    'PCI DSS v4.0': 'Req. 1.3.2',
    'DORA': 'Art. 9(4)(b)',
    'NIS2': 'Art. 21(2)(a)'
  },
  'Azure Confidential Computing': {
    'CSA CCM v4': 'CEK-03, DSP-03, DSP-08',
    'ISO 27001:2022': 'A.10.1.1, A.18.1.5',
    'NIST CSF 2.0': 'PR.DS-01, PR.DS-05',
    'CIS Controls v8': '3.11, 3.13',
    'SOC 2 Type II': 'CC6.7',
    'PCI DSS v4.0': 'Req. 3.5.1',
    'DORA': 'Art. 9(4)(e)',
    'NIS2': 'Art. 21(2)(h)'
  },
  'VPC Service Controls enabled': {
    'CSA CCM v4': 'IVS-06, DSP-16',
    'ISO 27001:2022': 'A.13.1.3, A.8.2.3',
    'NIST CSF 2.0': 'PR.AA-05, PR.IR-01',
    'CIS Controls v8': '9.2, 3.3',
    'SOC 2 Type II': 'CC6.1, CC6.6',
    'PCI DSS v4.0': 'Req. 1.3.1',
    'DORA': 'Art. 9(4)(b)',
    'NIS2': 'Art. 21(2)(a)'
  }
};

export const getFrameworkMappingForCheck = (checkTitle) => {
  return frameworkMappings[checkTitle] || {};
};

export const getAllFrameworks = () => {
  const frameworks = new Set();
  Object.values(frameworkMappings).forEach(mapping => {
    Object.keys(mapping).forEach(framework => {
      frameworks.add(framework);
    });
  });
  return Array.from(frameworks);
};
